import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';

import { flatten } from '@uirouter/core';
import { FiChecklist2ReportData } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report-data';
import {
    IBusinessUnitForReport,
    IChecklist2ForReport,
    IChecklist2ReportChecklistSummary,
    IChecklistScheduleForReport,
    IChecklistV2FilterAvailableItems,
    IChecklistV2ReportParameter,
    IChecklistV2ReportParameterFilter,
    ICheckpoint2GroupForReport,
    ICheckpointV2ForReport,
} from '../../../shared/fi-checklist2-report';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
    ISelectedFilterItem,
    ISharingMenuData,
} from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter/fi-items-list-filter';
import { INameIdObj, INameIdTypeObjWithChildren } from '@fi-shared/fi-shared-interfaces';
import { MatMenuTrigger } from '@angular/material/menu';
import { FiChecklist2ReportHelperService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-helper.service';
import { groupBy } from '@fi-shared/utils/group-by';
import {
    FiFilterType,
    FilterTypeLabelSet,
    SelectedItemsMaxAmountChecklistBenchmark,
} from '@fi-shared/fi-items-list/fi-items-list-filter/fi-filter-type-enum';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { FiDropdownItemDisabledFn } from '@fi-shared/fi-select-search/fi-select-search';
import {
    IWidgetTableFilterAvailableData,
    ChecklistWidgetTableFilterHelperService,
} from '@fi-shared/checklist/fi-checklist2-report/components/shared/widget-table-filter-helper.service';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { takeUntil } from 'rxjs/operators';
import { ILightWeightArea, ILightWeightObject } from '@fi-shared/models';
import { WidgetTableFilterMenuType } from '@fi-shared/checklist/fi-checklist2-report/components/shared/widget-table-filter/widget-table-filter-menu-type';
import { ObjectTypes } from '@fi-shared/types';
import { ChecklistWidgetTableFilterDataService } from '@fi-shared/checklist/fi-checklist2-report/components/shared/widget-table-filter-data.service';
import { WidgetTableFilterType } from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-type';
import { IMenuExpandableMultiSelectItem } from '@fi-shared/fi-menu-expandable-multi-select-item-search/fi-menu-expandable-multi-select-item-search.component';
import { FiChecklist2ReportDataService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-data.service';
import { FiCustomerDataService } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiReportTableFilterBaseComponent } from '@fi-shared/fi-report-table-filter/fi-report-table-filter-base.component';

@Component({
    selector: 'fi-checklist2-report-table-filter',
    templateUrl: './widget-table-filter.component.html',
    styleUrls: ['./widget-table-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist2-report-table-filter' },
    standalone: false,
})
export class FiChecklistReportTableFilterComponent
    extends FiReportTableFilterBaseComponent<IChecklistV2ReportParameterFilter, IChecklistV2FilterAvailableItems>
    implements OnInit, OnChanges, OnDestroy
{
    @Input({ required: true }) reportData: FiChecklist2ReportData;
    @Input({ required: true }) reportParameter: IChecklistV2ReportParameter;
    @Input() widgetTableFilterType: WidgetTableFilterType = WidgetTableFilterType.Common;
    @Input() isAllItemsRemovable: boolean = true;
    @Input() notRemovableFilterTypes: FiFilterType[] = [];

    @Input() availableChecklistIds: number[] = [];
    @Input() isChecklistLimited: boolean = false;
    @Input() isUnitRemovable: boolean = true;

    @Input() isResetFilterForceShown: boolean;
    @Input() isFilterIconShown: boolean;
    @Input() isUnitBenchmarkFilter: boolean;
    @Input() isDisabledFilterMenuTooltipTextIconShown: boolean = true;

    @Input() isChecklistReportFilter: boolean;
    @Input() isSectionsLayout: boolean;

    @Input() filterItemsToRemove$ = new Observable<ISelectedFilterItem[]>();
    @Input() clearSelectedItems$ = new Subject<void>();
    @Input() hasFilterUnsavedChanges: boolean;
    @Input() isSearchShown: boolean;
    @Input() skipTriggerFilterChangeEvent: boolean;

    @Output() searchChange = new EventEmitter<string>();
    @Output() selectedItemsChange = new EventEmitter<void>();
    @Output() scheduleChange = new EventEmitter<void>();

    @Input() isToggleButtonAvailable: boolean;
    @Input() isAttentionRequiredFilter: boolean;
    @Input() isSavedFiltersHidden: boolean = false;

    @Input() availableBusinessUnitIdsByData: number[];
    @Input() availableUserIdsByData: number[];
    @Input() availableChecklistScheduleIdsByData: number[];

    @Input() isMenuHighlightedFn: (menuItem: ISharingMenuData<INameIdObj>) => boolean = () => false;

    @ViewChild(MatMenuTrigger) matMenuTrigger: MatMenuTrigger;

    private availableData: IWidgetTableFilterAvailableData = {};
    private availableAreas: ILightWeightArea[];

    ObjectKeys = Object.keys;

    get isHistoryReport(): boolean {
        return this.widgetTableFilterDataService.isHistoryReport(this.widgetTableFilterType);
    }

    get isRecentActivityWidget(): boolean {
        return this.widgetTableFilterDataService.isRecentActivityWidget(this.widgetTableFilterType);
    }

    get historySummary(): IChecklist2ReportChecklistSummary[] {
        return this.reportData.getHistorySummary(this.reportParameter.Id);
    }

    constructor(
        injector: Injector,
        private fiChecklist2ReportHelperService: FiChecklist2ReportHelperService,
        private widgetTableFilterHelperService: ChecklistWidgetTableFilterHelperService,
        private widgetTableFilterDataService: ChecklistWidgetTableFilterDataService,
        private fiChecklist2ReportDataService: FiChecklist2ReportDataService,
        private customerDataService: FiCustomerDataService,
    ) {
        super(injector);
    }

    // Implement abstract methods from base class
    protected initMenusData(): void {
        this.menusData = this.widgetTableFilterHelperService.getFilterMenusData({
            availableItems: this.availableItems,
            availableAreas: this.availableAreas,
            availableDataFn: () => this.availableData,
            auditMode: this.reportData.auditMode,
            widgetTableFilterTypeFn: () => this.widgetTableFilterType,
            isChecklistReportFilter: this.isChecklistReportFilter,
            historyDataSummary: this.historySummary,
            isChecklistLimited: this.isChecklistLimited,
            availableChecklistIds: this.availableChecklistIds,
            availableChecklistScheduleIdsByData: this.availableChecklistScheduleIdsByData,
        });
    }

    protected setSelectedMenuItems(): void {
        this.setMenuSelectionFromFilter();
        this.updateMenusDisabledState();
    }

    // Override to use checklist-specific logic
    protected override setSelectedFilterItems(): void {
        this.selectedFilterItems = this.getSelectedFilterItemsFromFilter(this.currentFilter);
    }

    ngOnInit() {
        // Set availableItems from reportData
        this.availableItems = this.reportData.availableItems;

        // https://chainformation.atlassian.net/browse/FI-5562
        // we should use clone of available areas because their child items array can be changed
        this.availableAreas = cloneDeep(this.availableItems.Areas);

        // Call parent ngOnInit which handles basic setup
        super.ngOnInit();

        this.subscribeOnClearAll();
        this.subscribeOnRemoveItems();
    }

    private skipUpdatingSelectedFilterItems = false;
    ngOnChanges(changes: SimpleChanges) {
        const currentFilterChanged = changes.currentFilter && !changes.currentFilter.firstChange;

        if (currentFilterChanged && !this.skipUpdatingSelectedFilterItems) {
            // Call parent ngOnChanges first
            super.ngOnChanges(changes);
            this.removeUnavailableSelectedItems();
        }
        const availableBusinessUnitIdsChanged =
            changes.availableBusinessUnitIdsByData && !changes.availableBusinessUnitIdsByData.firstChange;

        if (availableBusinessUnitIdsChanged) {
            this.selectedFilterItems = this.getSelectedFilterItemsFromFilter(this.currentFilter);
            this.removeUnavailableSelectedItems();
        }

        const availableChecklistScheduleIdsByDataChanged = changes.availableChecklistScheduleIdsByData;
        if (
            availableChecklistScheduleIdsByDataChanged &&
            availableChecklistScheduleIdsByDataChanged.currentValue &&
            !availableChecklistScheduleIdsByDataChanged.isFirstChange()
        ) {
            const menuItem = this.menusData.find((item) => item.filterType === FiFilterType.Checklist);
            menuItem.menuItems = this.widgetTableFilterDataService.getHistoryChecklistsOptionsFilterData(
                this.reportData.availableItems,
                this.availableChecklistScheduleIdsByData,
            );
        }
    }

    private subscribeOnClearAll() {
        this.clearSelectedItems$.takeUntil(this.ngOnDestroy$).subscribe(() => {
            this.selectedFilterItems = this.getSelectedFilterItemsFromFilter(this.currentFilter);
            this.setSelectedFilterItemsGroups();
            this.setMenuSelectionFromFilter();

            this.cdr.markForCheck();
            this.onSelectedItemsChanged();
        });
    }

    private subscribeOnRemoveItems() {
        this.filterItemsToRemove$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((itemsToRemove: ISelectedFilterItem[]) => {
                itemsToRemove.forEach((item: ISelectedFilterItem) => this.removeSelectedItem(item, true));
            });
    }

    public isFilterHiddenByType(filterType: FiFilterType): boolean {
        return !this.menusData.find((menuData) => menuData.filterType === filterType)?.isAvailable?.();
    }

    @Input() isFilterLimited: boolean;
    getFilterLimitedMenuDescriptionTooltipText(menuData: ISharingMenuData<INameIdObj>): string {
        if (menuData.filterType === FiFilterType.Checklist) {
            if (menuData.childrenFilterType === FiFilterType.Schedule)
                return this.appTextService.get(
                    menuData.isSubmenuLayout
                        ? 'checklist.filter_max_checklists_schedules'
                        : 'checklist.unit_benchmark_schedule_limit',
                );
        }

        if (menuData.filterType === FiFilterType.Checkpoint)
            return this.appTextService.get('checklist.unit_benchmark_checkpoint_limit');

        if (menuData.filterType === FiFilterType.CheckpointGroup)
            return this.appTextService.get('checklist.unit_benchmark_checkpoint_group_limit');

        if (menuData.filterType === FiFilterType.BusinessUnit)
            return this.appTextService.get('checklist.unit_benchmark_unit_limit');
    }

    private setMenuSelectionFromFilter() {
        // set menu selected item according to current filter from NgOnInit and when filter changed
        this.widgetTableFilterHelperService.setMenuSelectionFromFilter({
            menusData: this.menusData,
            filter: this.currentFilter,
            isUnitBenchmarkFilter: this.isUnitBenchmarkFilter,
            isFoldedScheduleCheckpointMenus: this.isFoldedScheduleCheckpointMenus.bind(this),
            isChecklistReportFilter: this.isChecklistReportFilter,
            availableItems: this.availableItems,
        });
    }

    protected setSelectedFilterItemsGroups() {
        this.selectedFilterItemsGroups = groupBy(this.selectedFilterItems, 'filterType');
    }

    private getChecklistSummaryBusinessUnitIds(): number[] {
        return this.availableBusinessUnitIdsByData
            ? this.availableBusinessUnitIdsByData
            : flatten(
                  this.availableItems.Checklists.map((checklist: IChecklist2ForReport) => checklist.BusinessUnitIds),
              );
    }

    protected getSelectedFilterItemsFromFilter(filter: IChecklistV2ReportParameterFilter): ISelectedFilterItem[] {
        // on every selection change we should
        // calculate available options in menu (units, areas etc)
        // update disabled state of certain groups - areas, business areas etc
        // and generate selected items

        this.availableData = this.widgetTableFilterHelperService.getAvailableMenuItems({
            filter: filter,
            widgetTableFilterType: this.widgetTableFilterType,
            availableItems: this.availableItems,
            checklistSummaryBusinessUnitIds: this.getChecklistSummaryBusinessUnitIds(),
            isAttentionRequiredFilter: this.isAttentionRequiredFilter,
            availableAreas: this.availableAreas,
            availableBusinessUnitIdsByData: this.availableBusinessUnitIdsByData,
            availableUserIdsByData: this.availableUserIdsByData,
            availableChecklistScheduleIdsByData: this.availableChecklistScheduleIdsByData,
        });

        this.updateMenusDisabledState();

        return [
            ...this.fiChecklist2ReportHelperService.convertReportFilterSelectedIdToItem(
                filter.ChecklistIds,
                this.availableItems.Checklists,
                FiFilterType.Checklist,
            ),
            ...this.fiChecklist2ReportHelperService.convertReportFilterSelectedIdToItem(
                filter.ScheduleIds,
                this.availableItems.Schedules,
                FiFilterType.Schedule,
            ),
            ...this.fiChecklist2ReportHelperService.convertReportFilterSelectedIdToItem(
                filter.CheckpointIds,
                this.availableItems.Checkpoints,
                FiFilterType.Checkpoint,
            ),
            ...this.fiChecklist2ReportHelperService.convertReportFilterSelectedIdToItem(
                filter.CheckpointGroupIds,
                this.availableItems.CheckpointGroups,
                FiFilterType.CheckpointGroup,
            ),
            ...this.fiChecklist2ReportHelperService.convertReportFilterSelectedIdToItem(
                filter.BusinessUnitIds,
                this.availableItems.BusinessUnits,
                FiFilterType.BusinessUnit,
            ),
            ...this.fiChecklist2ReportHelperService.convertReportFilterSelectedIdToItem(
                filter.AreaIds,
                this.availableItems.Areas,
                FiFilterType.Area,
            ),
            ...this.fiChecklist2ReportHelperService.convertReportFilterSelectedIdToItem(
                filter.BusinessAreaIds,
                this.availableItems.BusinessAreas,
                FiFilterType.BusinessArea,
            ),
            ...this.fiChecklist2ReportHelperService.convertReportFilterSelectedIdToItem(
                filter.UserIds,
                this.availableItems.Users,
                FiFilterType.User,
                'FullName',
            ),
            ...this.fiChecklist2ReportHelperService.convertReportFilterSelectedIdToItem(
                filter.CheckpointStatusTypes,
                this.widgetTableFilterHelperService.getAnswerMenuItems(),
                FiFilterType.CheckpointStatus,
            ),
        ];
    }

    private updateMenusDisabledState() {
        this.widgetTableFilterHelperService.setMenuDisabledState({
            menusData: this.menusData,
            filter: this.currentFilter,
            isUnitBenchmarkFilter: this.isUnitBenchmarkFilter,
            isFoldedScheduleCheckpointMenus: this.isFoldedScheduleCheckpointMenus.bind(this),
            isChecklistReportFilter: this.isChecklistReportFilter,
            availableItems: this.availableItems,
        });
    }

    isSelectedItemRemovable(filterType: FiFilterType): boolean {
        return this.widgetTableFilterHelperService.isSelectedItemRemovable(
            this.widgetTableFilterType,
            filterType,
            this.isAllItemsRemovable,
            this.currentFilter,
            this.notRemovableFilterTypes,
            this.availableItems.BusinessUnits.length,
            this.reportData.auditMode,
        );
    }

    isCheckpointMenu(filterType: FiFilterType): boolean {
        return filterType === FiFilterType.Checkpoint;
    }

    getFilterMenuType(menuData: ISharingMenuData<INameIdObj>): WidgetTableFilterMenuType {
        return this.widgetTableFilterHelperService.getFilterMenuType(
            menuData,
            this.widgetTableFilterType,
            this.isHistoryReport,
            this.isRecentActivityWidget,
        );
    }

    private isFilterWithSubMenu(menuData: ISharingMenuData<INameIdObj>): boolean {
        return this.getFilterMenuType(menuData) === WidgetTableFilterMenuType.WithSubMenu;
    }
    private isFoldedScheduleCheckpointMenus(menuDataItem: ISharingMenuData<INameIdObj>): boolean {
        return this.getFilterMenuType(menuDataItem) === WidgetTableFilterMenuType.FoldedSchedules;
    }
    private isChecklistScheduleMenuData(menuData: ISharingMenuData<INameIdObj>): boolean {
        return menuData.filterType === FiFilterType.Schedule;
    }
    private isFoldedFilterMenu(filterType: FiFilterType): boolean {
        return this.widgetTableFilterHelperService.isFoldedFilterMenu(
            filterType,
            this.widgetTableFilterType,
            this.isHistoryReport,
            this.isRecentActivityWidget,
        );
    }

    onSearchChange(searchValue: string) {
        this.searchChange.emit(searchValue);
    }

    removeSelectedItem(selectedItem: ISelectedFilterItem, doRemoveAllRelatedItems?: boolean) {
        this.removeItemFromMenuSelection(selectedItem);
        this.removeAllRelatedChecklistItems(selectedItem, doRemoveAllRelatedItems);
        this.removeAllRelatedChecklistScheduleItems(selectedItem, doRemoveAllRelatedItems);
        this.selectedItemsChange.emit();
        this.setSelectedFilterItemsGroups();
    }

    isFilterItemSelectedById(itemId: number): boolean {
        return !!this.selectedFilterItems.find(
            (selectedFilterItem: ISelectedFilterItem) => selectedFilterItem.value === itemId,
        );
    }

    private removeAllRelatedChecklistScheduleItems(
        selectedItem: ISelectedFilterItem,
        doRemoveAllRelatedItems: boolean,
    ) {
        if (doRemoveAllRelatedItems && selectedItem?.filterType === FiFilterType.Schedule) {
            const relatedChecklistId: number = this.widgetTableFilterHelperService.getRelatedChecklistIdByScheduleId(
                selectedItem.value as number,
                this.availableItems.Checklists,
            );
            const checklistRelatedScheduleIds: number[] = this.widgetTableFilterDataService.getRelatedSchedulesIds(
                relatedChecklistId,
                this.availableItems.Checklists,
            );
            const isAllRelatedSchedulesNotSelected: boolean = checklistRelatedScheduleIds.every(
                (checklistRelatedScheduleId: number) => !this.isFilterItemSelectedById(checklistRelatedScheduleId),
            );

            if (isAllRelatedSchedulesNotSelected) {
                this.removeSelectedItem(
                    this.widgetTableFilterHelperService.getSelectedFilterChecklistItemById(
                        this.selectedFilterItems,
                        relatedChecklistId,
                    ),
                    true,
                );
            }

            if (this.isHistoryReport || this.isUnitBenchmarkFilter) {
                this.removeInaccessibleFilterItems();
            } else {
                this.removeSelectedUnitsRelatedToChecklist();
            }
        }
    }

    private removeInaccessibleFilterItems() {
        if (this.isUnitBenchmarkFilter && this.isNoSelectedItemByType(FiFilterType.Schedule)) {
            this.clearFilter();
            this.selectedFilterItems = this.getSelectedFilterItemsFromFilter(this.currentFilter);
            this.setSelectedFilterItemsGroups();
            return;
        }
        this.removeUnavailableSelectedItems();
    }

    private removeUnavailableSelectedItems() {
        const availableUnitIds: number[] = this.availableData.BusinessUnits.map(
            (businessUnit: IBusinessUnitForReport) => businessUnit.Id,
        );
        const selectedUnitsToRemove: ISelectedFilterItem[] = this.selectedFilterItems.filter(
            (filterItem: ISelectedFilterItem) => {
                return (
                    filterItem.filterType === FiFilterType.BusinessUnit &&
                    !availableUnitIds.includes(filterItem.value as number)
                );
            },
        );
        const availableAreaIds: number[] = this.availableData.Areas.map((area: ILightWeightArea) => area.Id);
        const selectedAreasToRemove: ISelectedFilterItem[] = this.selectedFilterItems.filter(
            (filterItem: ISelectedFilterItem) => {
                return (
                    filterItem.filterType === FiFilterType.Area &&
                    !availableAreaIds.includes(filterItem.value as number)
                );
            },
        );
        const availableBusinessAreaIds: number[] = this.availableData.BusinessAreas.map(
            (bArea: ILightWeightObject) => bArea.Id,
        );
        const selectedBusinessAreasToRemove: ISelectedFilterItem[] = this.selectedFilterItems.filter(
            (filterItem: ISelectedFilterItem) => {
                return (
                    filterItem.filterType === FiFilterType.BusinessArea &&
                    !availableBusinessAreaIds.includes(filterItem.value as number)
                );
            },
        );

        [...selectedUnitsToRemove, ...selectedAreasToRemove, ...selectedBusinessAreasToRemove].forEach(
            (selectedItems: ISelectedFilterItem) => this.removeItemFromMenuSelection(selectedItems),
        );
    }

    private removeSelectedUnitsRelatedToChecklist() {
        if (!this.isRecentActivityWidget) return;

        const selectedScheduleIds: number[] = this.selectedFilterItems
            .filter((filterItem: ISelectedFilterItem) => filterItem.filterType === FiFilterType.Schedule)
            .map((filterItem: ISelectedFilterItem) => filterItem.value as number);
        const availableUnitIds: number[] = flatten(
            this.historySummary
                .filter((summary: IChecklist2ReportChecklistSummary) =>
                    selectedScheduleIds.includes(summary.ScheduleId),
                )
                .map((summary: IChecklist2ReportChecklistSummary) => summary.AllUniqueUnitIds),
        );
        const unitsToRemove: ISelectedFilterItem[] = this.selectedFilterItems.filter(
            (filterItem: ISelectedFilterItem) => {
                return (
                    filterItem.filterType === FiFilterType.BusinessUnit &&
                    !availableUnitIds.includes(filterItem.value as number)
                );
            },
        );
        unitsToRemove.forEach((unitToRemove: ISelectedFilterItem) => this.removeItemFromMenuSelection(unitToRemove));
    }

    private removeAllRelatedChecklistItems(selectedItem: ISelectedFilterItem, doRemoveAllRelatedItems: boolean) {
        if (doRemoveAllRelatedItems && selectedItem?.filterType === FiFilterType.Checklist) {
            this.widgetTableFilterHelperService
                .getSelectedScheduleFilterItems(
                    this.selectedFilterItems,
                    selectedItem.value as number,
                    this.availableItems.Checklists,
                )
                .forEach((selectedFilterItem: ISelectedFilterItem) =>
                    this.removeSelectedItem(selectedFilterItem, true),
                );
        }
    }

    private isNoSelectedItemByType(filterType: FiFilterType): boolean {
        return !this.selectedFilterItems.filter((selectedItem: ISelectedFilterItem) => {
            return selectedItem.filterType === filterType;
        })?.length;
    }

    private removeItemFromMenuSelection(selectedItem: ISelectedFilterItem) {
        if (!isDefined(selectedItem)) return;

        const index: number = this.selectedFilterItems.findIndex((filterItem: ISelectedFilterItem) => {
            return filterItem.value === selectedItem.value && filterItem.filterType === selectedItem.filterType;
        });
        this.selectedFilterItems.splice(index, 1);
        const selectedItemsByType: ISelectedFilterItem[] =
            this.widgetTableFilterHelperService.getSelectedFilterItemsByType(
                this.selectedFilterItems,
                selectedItem.filterType,
            );
        const isFoldedFilterMenu: boolean = this.isFoldedFilterMenu(selectedItem.filterType);
        const currentMenuData: ISharingMenuData<INameIdObj> =
            this.widgetTableFilterHelperService.getMenuDataByFilterType(
                this.menusData,
                selectedItem.filterType,
                isFoldedFilterMenu,
            );
        const currentFilterMenuType: WidgetTableFilterMenuType = this.getFilterMenuType(currentMenuData);
        const selectedItemIds: number[] = selectedItemsByType.map((item: ISelectedFilterItem) => item.value as number);

        if (isFoldedFilterMenu) {
            currentMenuData.selectedChildItemIds = selectedItemIds;
            this.sharingWithItemChange(currentMenuData.childrenFilterType, currentMenuData.selectedChildItemIds);
        } else if (currentFilterMenuType === WidgetTableFilterMenuType.Expandable) {
            // expandable filter menu has whole item in selected items (not Id)
            currentMenuData.selectedExpandableItems = currentMenuData.menuItems.filter((item) => {
                const expandableItem = item as INameIdTypeObjWithChildren;
                return (
                    expandableItem.ObjectType === currentMenuData.selectionObjectTypes[0] &&
                    selectedItemIds.includes(expandableItem.Id)
                );
            });
            this.sharingWithItemChange(currentMenuData.filterType, selectedItemIds);
        } else if (currentFilterMenuType === WidgetTableFilterMenuType.AlignedWithChildren) {
            let selectionData;
            if (selectedItem.filterType === FiFilterType.Area) {
                selectionData = this.widgetTableFilterHelperService.getSelectedAreaIdsForFilter(
                    this.availableAreas,
                    selectedItemIds,
                );
            }
            currentMenuData.selectedItemIds = selectionData.selectedItems ?? [];
            currentMenuData.selectedChildItemIds = selectionData.selectedChildItems ?? [];
            this.sharingWithItemChange(currentMenuData.filterType, [
                ...currentMenuData.selectedItemIds,
                ...currentMenuData.selectedChildItemIds,
            ]);
        } else {
            currentMenuData.selectedItemIds = selectedItemIds;
            this.sharingWithItemChange(currentMenuData.filterType, selectedItemIds);
        }
        this.updateMenuSelectedItems(currentMenuData);
    }

    isSearchInputShown(): boolean {
        return this.isSearchShown && !this.isChecklistReportFilter;
    }

    isFilterActionsShown(): boolean {
        return !this.isChecklistReportFilter;
    }

    onScheduleChange(selectedSchedule: IChecklistScheduleForReport) {
        if (this.isChecklistReportFilter) {
            const scheduleMenuData: ISharingMenuData<INameIdObj> = this.menusData.find(
                (menuData: ISharingMenuData<INameIdObj>) => {
                    return this.isChecklistScheduleMenuData(menuData);
                },
            );
            scheduleMenuData.selectedItemIds = [selectedSchedule.Id];
            this.updateMenuSelectedItems(scheduleMenuData);
            this.cdr.detectChanges();
            this.sharingWithItemChange(scheduleMenuData.filterType, scheduleMenuData.selectedItemIds);
            this.scheduleChange.emit();
        }
    }

    protected sharingWithItemChange(itemFilterType: FiFilterType, selectedItems: number[]) {
        const filterPropertyName: string = this.itemsListFilterHelperService.filterTypeToPropertyNameRecord[itemFilterType];

        if (!selectedItems.length) {
            delete this.currentFilter[filterPropertyName];
        } else {
            this.currentFilter[filterPropertyName] = selectedItems;
        }
        this.cdr.markForCheck();
        this.selectedFilterItems = this.getSelectedFilterItemsFromFilter(this.currentFilter);
        this.setSelectedFilterItemsGroups();

        if (this.isFilterLimited) {
            this.showMaxItemsSelectedWarning(itemFilterType, selectedItems);
        }
    }

    sharingWithExpandableItemChanged(
        selectedItems: INameIdTypeObjWithChildren[],
        menuData: ISharingMenuData<INameIdTypeObjWithChildren>,
    ) {
        this.skipUpdatingSelectedFilterItems = true;
        setTimeout(() => {
            this.skipUpdatingSelectedFilterItems = false;
        });
        let selectedItemIds: number[];
        if (menuData.filterType === FiFilterType.CheckpointGroup) {
            selectedItemIds = selectedItems
                .filter((item) => item.ObjectType === ObjectTypes.CheckpointGroup)
                .map((item) => item.Id);
        } else if (menuData.filterType === FiFilterType.Checkpoint) {
            selectedItemIds = selectedItems
                .filter((item) => item.ObjectType === ObjectTypes.Checkpoint2)
                .map((item) => item.Id);
        } else {
            throw new Error(`sharingWithExpandableItemChanged: unknown filter type ${menuData.filterType}`);
        }
        this.sharingWithItemChange(menuData.filterType, selectedItemIds);
        this.onSelectedItemsChanged();
        // checkpoint group or checkpoint selecting changed
    }

    maxAmountItemsSelectedWarningTooltipFilterType: FiFilterType;
    private readonly maxAmountItemsSelectedWarningTimeout: number = 1500;
    private showMaxItemsSelectedWarning(itemFilterType: FiFilterType, selectedItemIds: number[]) {
        const maxSelectedItemsAmount: number = SelectedItemsMaxAmountChecklistBenchmark[itemFilterType];
        if (!isDefined(maxSelectedItemsAmount) || this.isCheckpointOrCheckpointGroupItem(itemFilterType)) {
            return;
        }

        if (selectedItemIds.length === maxSelectedItemsAmount) {
            this.matMenuTrigger.closeMenu();

            this.maxAmountItemsSelectedWarningTooltipFilterType = itemFilterType;
            setTimeout(() => {
                this.maxAmountItemsSelectedWarningTooltipFilterType = undefined;
                this.cdr.markForCheck();
            }, this.maxAmountItemsSelectedWarningTimeout);
        }
    }

    isMaxAmountItemsSelectedWarningShown(filterType: FiFilterType): boolean {
        return filterType === this.maxAmountItemsSelectedWarningTooltipFilterType;
    }

    isLimitTooltipShown(menuDataItem: ISharingMenuData<INameIdObj>): boolean {
        return this.isMaxAmountItemsSelectedWarningShown(menuDataItem.filterType);
    }

    menuDataMainItemsSelectionChangeEqualChildrenType(menuData: ISharingMenuData<INameIdObj>) {
        this.sharingWithItemChange(FiFilterType.Area, [...menuData.selectedItemIds, ...menuData.selectedChildItemIds]);
        this.onSelectedItemsChanged();
    }

    menuDataMainItemsSelectionChange(menuData: ISharingMenuData<INameIdObj>) {
        this.onSelectedItemsChanged();
        this.sharingWithItemChange(menuData.filterType, menuData.selectedItemIds);
    }

    onRemovingChecklist(checklistId: number) {
        const selectedChecklistFilterItem: ISelectedFilterItem =
            this.widgetTableFilterHelperService.getSelectedFilterChecklistItemById(
                this.selectedFilterItems,
                checklistId,
            );
        this.removeAllRelatedChecklistItems(selectedChecklistFilterItem, true);
    }

    menuDataMainChildrenSelectionChangeEqualChildrenType(menuData: ISharingMenuData<INameIdObj>) {
        this.sharingWithItemChange(FiFilterType.Area, [...menuData.selectedItemIds, ...menuData.selectedChildItemIds]);
        const updateMenu: ISharingMenuData<INameIdObj> = this.widgetTableFilterHelperService.getMenuDataByFilterType(
            this.menusData,
            FiFilterType.Area,
            false,
        );
        updateMenu.selectedItemIds = menuData.selectedItemIds;
        updateMenu.selectedChildItemIds = menuData.selectedChildItemIds;
        this.updateMenuSelectedItems(updateMenu);
        return;
    }

    menuDataMainChildrenSelectionChange(menuData: ISharingMenuData<INameIdObj>) {
        this.sharingWithItemChange(menuData.childrenFilterType, menuData.selectedChildItemIds);
        this.onSelectedItemsChanged();

        const isChecklistMenu: boolean = this.isFilterWithSubMenu(menuData);
        if (isChecklistMenu) {
            this.removeSelectedUnitsRelatedToChecklist();
        }
        const menuFilterType: FiFilterType = isChecklistMenu ? FiFilterType.Checklist : FiFilterType.Schedule;
        const updateMenu: ISharingMenuData<INameIdObj> = this.widgetTableFilterHelperService.getMenuDataByFilterType(
            this.menusData,
            menuFilterType,
            !isChecklistMenu,
        );
        updateMenu.selectedItemIds = menuData.selectedItemIds;
        updateMenu.selectedChildItemIds = menuData.selectedChildItemIds;
        this.updateMenuSelectedItems(updateMenu);
    }

    private updateMenuSelectedItems(newMenuData: ISharingMenuData<INameIdObj>) {
        this.menusData = this.menusData.map((menuData: ISharingMenuData<INameIdObj>) => {
            if (menuData.filterType === newMenuData.filterType) {
                menuData.selectedItemIds = newMenuData.selectedItemIds;
                if (
                    (this.isHistoryReport || !this.isRecentActivityWidget) &&
                    menuData.childrenFilterType === newMenuData.childrenFilterType
                ) {
                    menuData.selectedChildItemIds = newMenuData.selectedChildItemIds;
                }
            }

            return menuData;
        });
        this.onSelectedItemsChanged();
    }

    selectFilter(filterGuid: string) {
        this.onSelectFilter.emit(filterGuid);
        //this.selectedItemsChange.emit();
        setTimeout(() => {
            this.onSelectedItemsChanged();
        });
    }

    clearFilter() {
        delete this.currentFilter.ChecklistIds;
        delete this.currentFilter.ScheduleIds;
        delete this.currentFilter.CheckpointIds;
        delete this.currentFilter.CheckpointGroupIds;
        delete this.currentFilter.BusinessAreaIds;
        delete this.currentFilter.AreaIds;
        delete this.currentFilter.BusinessUnitIds;
        delete this.currentFilter.UserIds;
        delete this.currentFilter.CheckpointStatusTypes;
        this.currentFilterChange.emit(this.currentFilter);
        this.clearSelectedItems$.next();
    }

    isCustomReportWidget(): boolean {
        return this.fiChecklist2ReportHelperService.isCustomReportWidget(this.reportParameter);
    }

    isClearFilterButtonShown(): boolean {
        return (
            (!this.isCustomReportWidget() || this.isResetFilterForceShown) &&
            !this.fiChecklist2ReportHelperService.isFilterEmpty(this.currentFilter)
        );
    }

    showSavedFilters(): boolean {
        return !!this.savedFilters?.length && !this.isCustomReportWidget();
    }

    private isCurrentFilterSavedFilter(): boolean {
        return !!this.savedFilters?.find((savedFilter) => savedFilter.Guid === this.currentFilter.Guid);
    }

    isUpdateFilterButtonVisible(): boolean {
        return this.hasFilterUnsavedChanges && !this.isCustomReportWidget() && this.isCurrentFilterSavedFilter();
    }

    isResetFilterButtonVisible(): boolean {
        return this.hasFilterUnsavedChanges && (this.isResetFilterForceShown || this.isCustomReportWidget());
    }

    updateCurrentFilter() {
        this.onUpdateCurrentFilter.emit();
    }

    protected resetCurrentFilter() {
        this.onResetCurrentFilter.emit();
        setTimeout(() => {
            this.onSelectedItemsChanged();
        });
    }

    private triggerFilterChangedAtReportData() {
        this.currentFilterChange.emit(this.currentFilter);
        if (!this.skipTriggerFilterChangeEvent) {
            this.fiChecklist2ReportDataService.triggerFilterChanged(this.reportParameter.Id);
        }
    }

    onSelectedItemsChanged() {
        this.selectedItemsChange.emit();

        // https://chainformation.atlassian.net/browse/FI-5689
        // now both open checklists and history should have same behavior
        this.triggerFilterChangedAtReportData();
    }

    getFilterMenuDisabledTextByType(menuData: ISharingMenuData<INameIdObj>): string {
        if (!this.isHistoryReport && menuData.filterType === FiFilterType.Checklist) {
            return this.appTextService.get('checklist.filter_disabled_checklist_tooltip');
        }

        if (this.isUnitBenchmarkFilter) {
            return this.appTextService.get('checklist.unit_benchmark_select_unit_tooltip_text');
        }

        if (menuData.filterType === FiFilterType.Checkpoint) {
            if (this.currentFilter.ChecklistIds?.length > 1) {
                return this.appTextService.get('checklist.filter_disabled_checkpoint_by_checklist_tooltip');
            }
            if (this.currentFilter.CheckpointGroupIds.length > 0) {
                return this.appTextService.get('checklist.filter_disabled_checkpoint_by_groups_tooltip');
            }
        }
        if (menuData.filterType === FiFilterType.CheckpointGroup) {
            if (this.currentFilter.ChecklistIds?.length > 1) {
                return this.appTextService.get('checklist.filter_disabled_checkpoint_group_by_checklist_tooltip');
            }
            if (this.currentFilter.CheckpointIds.length > 0) {
                return this.appTextService.get('checklist.filter_disabled_checkpoint_group_by_checkpoints_tooltip');
            }
        }

        if (menuData.filterType === FiFilterType.Area) {
            if (this.currentFilter.BusinessUnitIds.length > 0) {
                return this.appTextService.get('checklist.filter_disabled_area_by_units_tooltip');
            }
        }

        if (menuData.filterType === FiFilterType.BusinessArea) {
            if (this.currentFilter.BusinessUnitIds.length > 0) {
                return this.appTextService.get('checklist.filter_disabled_business_area_by_units_tooltip');
            }
        }
    }

    getMenuItemsDisabledText(data: ISharingMenuData<INameIdObj>): string {
        if (this.isScheduleMenuOnHistoryReport(data.childrenFilterType)) {
            if (this.currentFilter.CheckpointIds?.length > 0) {
                return this.appTextService.get('checklist.filter_disabled_checklist_tooltip');
            }
            if (this.currentFilter.CheckpointGroupIds?.length > 0) {
                return this.appTextService.get('checklist.filter_disabled_checklist_item_by_groups_tooltip');
            }
        }

        return undefined;
    }

    isTooltipDisabledFilterMenuAvailable(menuData: ISharingMenuData<INameIdObj>): boolean {
        return menuData.isDisabled && !!this.getFilterMenuDisabledTextByType(menuData);
    }

    getOptionDisabledFnByFilterType(data: ISharingMenuData<INameIdObj>): FiDropdownItemDisabledFn<INameIdObj> {
        if (data.filterType === FiFilterType.Schedule && this.isHistoryReport)
            return (item: INameIdObj, allItems: INameIdObj[]) => {
                return (
                    this.currentFilter.ScheduleIds?.includes(item.Id) && this.currentFilter.ScheduleIds?.length === 1
                );
            };

        if (data.filterType === FiFilterType.BusinessUnit && this.isUnitBenchmarkFilter)
            return this.getExternalItemDisabledFn(data);

        return () => false;
    }

    getExternalFilterFn(data: ISharingMenuData<INameIdObj>): FiDropdownItemDisabledFn<INameIdObj> {
        if (data.isOptionHidden) {
            return (item: INameIdObj, allItems: INameIdObj[]) => {
                return !data.isOptionHidden(item);
            };
        }
        return () => true;
    }

    private isMaxAmountItemsPerChecklist(
        itemFilterType: FiFilterType,
        selectedItemIds: number[] = [],
        currentItemId: number,
    ): boolean {
        const maxSelectedItemsAmount = SelectedItemsMaxAmountChecklistBenchmark[itemFilterType];
        const currentAvailableItems = this.getAvailableItemsByFilterType(itemFilterType);
        const selectedItemsData = selectedItemIds.map((id) => currentAvailableItems.find((item) => item.Id === id));

        if (!selectedItemsData?.length) {
            return false;
        }

        const currentItem = currentAvailableItems.find((item) => item.Id === currentItemId);
        const currentChecklistItems = selectedItemsData.filter((item) => item.ChecklistId === currentItem?.ChecklistId);

        return currentChecklistItems.length === maxSelectedItemsAmount;
    }

    private getAvailableItemsByFilterType(
        filterType: FiFilterType,
    ): ICheckpointV2ForReport[] | ICheckpoint2GroupForReport[] {
        return filterType === FiFilterType.Checkpoint
            ? this.availableItems.Checkpoints
            : this.availableItems.CheckpointGroups;
    }

    getExternalItemDisabledFn(data: ISharingMenuData<INameIdObj>): FiDropdownItemDisabledFn<INameIdObj> {
        if (!this.isUnitBenchmarkFilter) {
            return () => false;
        }

        const isRelevantFilterType =
            [FiFilterType.BusinessUnit, FiFilterType.Checkpoint, FiFilterType.CheckpointGroup].includes(
                data.filterType,
            ) || data.childrenFilterType === FiFilterType.Schedule;
        if (!isRelevantFilterType) {
            return () => false;
        }

        const targetFilterType =
            data.childrenFilterType === FiFilterType.Schedule ? FiFilterType.Schedule : data.filterType;
        const selectedItems = this.selectedFilterItems.filter((item) => item.filterType === targetFilterType);
        const maxSelectedItemsAmount = SelectedItemsMaxAmountChecklistBenchmark[targetFilterType];

        return (item: INameIdObj | IMenuExpandableMultiSelectItem<any>) => {
            return this.isItemDisabled(item, selectedItems, targetFilterType, maxSelectedItemsAmount);
        };
    }

    private isItemDisabled(
        item: INameIdObj | IMenuExpandableMultiSelectItem<any>,
        selectedItems: ISelectedFilterItem[],
        targetFilterType: FiFilterType,
        maxSelectedItemsAmount: number,
    ): boolean {
        const isItemNotSelected = !selectedItems?.find((selectedItem) => selectedItem.value === item.Id);
        const selectedItemIds = selectedItems?.map((item) => item.value as number) || [];

        const isMaxAmountSelected = this.isCheckpointOrCheckpointGroupItem(targetFilterType)
            ? this.isMaxAmountItemsPerChecklist(targetFilterType, selectedItemIds, item.Id)
            : selectedItems?.length === maxSelectedItemsAmount;

        return isMaxAmountSelected && isItemNotSelected;
    }

    private isCheckpointOrCheckpointGroupItem(filterType: FiFilterType): boolean {
        return filterType === FiFilterType.Checkpoint || filterType === FiFilterType.CheckpointGroup;
    }

    getMainItemDisabledFn(data: ISharingMenuData<INameIdObj>): FiDropdownItemDisabledFn<INameIdObj> {
        if (
            this.isScheduleMenuOnHistoryReport(data.childrenFilterType) ||
            this.widgetTableFilterDataService.isAttachmentsList(this.widgetTableFilterType)
        ) {
            // https://chainformation.atlassian.net/browse/FI-6139
            // To avoid overlap, disable multi-checklist selection when “Sections” is selected, same logic we have currently for “Checkpoints”.
            const selectedCheckpointsCount: number = this.selectedFilterItems.filter((item) => {
                return item.filterType === FiFilterType.Checkpoint;
            }).length;

            const selectedCheckpointGroupsCount: number = this.selectedFilterItems.filter((item) => {
                return item.filterType === FiFilterType.CheckpointGroup;
            }).length;

            const selectedChecklistIds: number[] = this.selectedFilterItems
                .filter((item) => item.filterType === FiFilterType.Checklist)
                .map((item) => item.value as number);
            return (item: INameIdObj) => {
                return (
                    (!!selectedCheckpointsCount || !!selectedCheckpointGroupsCount) &&
                    !selectedChecklistIds.includes(item.Id)
                );
            };
        }
        return () => false;
    }

    private isScheduleMenuOnHistoryReport(childrenFilterType: FiFilterType): boolean {
        return this.isNonRecentActivityUnitBenchmarkHistoryReport() && childrenFilterType === FiFilterType.Schedule;
    }

    isNonRecentActivityUnitBenchmarkHistoryReport(): boolean {
        return (
            (this.isHistoryReport && !this.isUnitBenchmarkFilter && !this.isRecentActivityWidget) ||
            this.widgetTableFilterDataService.isAttachmentsList(this.widgetTableFilterType)
        );
    }

    isSelectedItemsVisible$ = new BehaviorSubject<boolean>(true);
    toggleSelectedItemsVisibility() {
        this.isSelectedItemsVisible$.next(!this.isSelectedItemsVisible$.value);
    }

    getSelectedFilterOptionsCount(): number {
        return Object.values(this.currentFilter)
            .filter(Array.isArray)
            .reduce((counter, arr) => counter + arr.length, 0);
    }

    // note: if you decide to change class name, change also in styles;
    private readonly _highlightedMenuClassName = 'filter-menu-highlighted';
    getFilterMenuClassName(menuItem: ISharingMenuData<INameIdObj>) {
        return {
            [this._highlightedMenuClassName]: this.isMenuHighlightedFn(menuItem),
        };
    }

    isSelectedFilterOptionsToggleButtonShown(): boolean {
        return this.isToggleButtonAvailable && !!this.selectedFilterItems.length;
    }

    isTestCustomer() {
        return this.customerDataService.isTestCustomer();
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly FilterTypeLabelSet: { [key in FiFilterType]?: string } = FilterTypeLabelSet;
    protected readonly FiInfoIconMode = FiInfoIconMode;
    protected readonly WidgetTableFilterMenuType = WidgetTableFilterMenuType;
    protected readonly WidgetTableFilterType = WidgetTableFilterType;
}
